<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsKiosks } from "../data/getFaqsKiosks.js";
	import { getKiosksHowDev } from "../data/getKiosksHowDev.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getReasonsKiosks } from "../data/getReasonsKiosks.js";
	import { getServices } from "../data/getServices.js";
	import type { KiosksProps } from "./KiosksProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: KiosksProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Díky profi cg[[kiosku]] zlepšíte zákaznický zážitek a cg[[snížíte náklady]]  na personál" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Pro malé i velké firmy [{getDevelopmentPath(
						tenant,
					)}][[vyvíjíme]]]] sp[[kiosková řešení na zakázku]] – od samoobslužných kiosků v gastronomii až po informační terminály v logistice. Zařídíme i propojení s tiskárnou účtenek, kamerou či platebním terminálem."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Nezávazně poptat" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Nechte si cg[[vyvinout]] kiosek na přání, který cg[[zrychlí]] růst vašeho podnikání" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Podnikat v gastronomii, dopravě, logistice nebo volnočasovém sektoru bez uživatelsky přívětivých kiosků? To je v dnešní technologické době nepředstavitelné. sp[[Kvalitním kioskovým řešením dopřejete svým zákazníkům lepší zážitek]] z nakupování, zjednodušíte objednávkové procesy nebo zvýšíte efektivitu svých zaměstnanců."
				/>
			</p>
			<p>
				<Formatted
					text="sp[[Tým Fastest pro vás vyvine interaktivní kiosek na míru vašemu podnikání a potřebám, který bude nejen dobře vypadat, ale hlavně spolehlivě fungovat.]] S pomocí našich partnerů pro vás za výhodnou cenu zajistíme i kompletní hardware ke kiosku."
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		noPadding="bot"
		gridHeadline="container-8 lg:text-center"
		gridDesc="container-8"
		centered
	>
		{#snippet headline()}
			<Formatted text="S čím vám v rámci kioskových řešení cg[[pomůžeme]]?" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getReasonsKiosks(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Jak to proběhne, když začneme cg[[spolupracovat]]" />
			<span class="block text-2md font-bold mt-6"
				><Formatted
					text="Provedeme vás celým procesem od úvodní konzultace přes vývoj až po zaškolení před používáním kiosku."
				/></span
			>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getKiosksHowDev(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			><Formatted text="Rezervovat online schůzku" />
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6" gridDesc="container-6" paddingFollowNext>
		{#snippet headline()}
			<Formatted text="Postaráme se také o tvorbu cg[[webu]] nebo vývoj cg[[mobilní aplikace]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Vývoj je náš každodenní chleba nejen v oblasti kiosků.]] Rádi si vezmeme na starost i tvorbu firemních webových stránek, ale i programování webové či mobilní aplikace."
				/>
			</p>
		{/snippet}
	</Section>
	<ServicesCards cards={getServices(tenant)} />

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[vývoji kioskových řešení]]" />
		{/snippet}
		<FaqItems faqs={getFaqsKiosks(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
